import { Badge, Box, Code, Flex, Text } from '@chakra-ui/react';
import { FunctionComponent } from 'react';
import {
  BATCH_JOB_STATUS,
  BATCH_JOB_STATUS_BADGE_COLOR,
  BATCH_JOB_STATUS_LABELS,
  DATA_PROCESSING_EXPECTED_DURATION,
  DATA_PROCESSING_POLL_INTERVAL_MS,
  DEFAULT_BADGE_COLOR,
} from '../../constants';
import { useDataProcessingJob } from '../../useDataProcessingJob';

const formatTime = (epochMs?: number | null) => (epochMs ? new Date(epochMs).toLocaleTimeString() : null);

/** Details of the current Batch job, shown inside the "Data processing" task. */
export const DataProcessingStatus: FunctionComponent = () => {
  const { jobId, job, isSucceeded, isFailed, lastCheckedAt, refreshError } = useDataProcessingJob();
  // Before the first status response the job is known to be submitted.
  const status = job?.status ?? BATCH_JOB_STATUS.SUBMITTED;
  const pollSeconds = Math.round(DATA_PROCESSING_POLL_INTERVAL_MS / 1000);

  return (
    <Box
      bg={isFailed ? 'red.50' : isSucceeded ? 'green.50' : 'marketcast.primaryCta25.1'}
      border="1px solid"
      borderColor={isFailed ? 'red.200' : isSucceeded ? 'green.200' : 'marketcast.gray.5'}
      borderRadius="8px"
      p={3}
      role="status"
      aria-live="polite"
    >
      <Flex direction="column" gap={2} fontSize="xs" color="marketcast.text.primary">
        <Flex gap={2} align="center" wrap="wrap">
          <Badge colorScheme={BATCH_JOB_STATUS_BADGE_COLOR[status] ?? DEFAULT_BADGE_COLOR}>{status}</Badge>
          <Text color="marketcast.gray.2">{BATCH_JOB_STATUS_LABELS[status]}</Text>
        </Flex>

        {!isSucceeded && !isFailed && (
          <Text color="marketcast.gray.2">
            Data processing in progress. This can take {DATA_PROCESSING_EXPECTED_DURATION}; the status refreshes every{' '}
            {pollSeconds} seconds.
          </Text>
        )}
        {isSucceeded && <Text color="marketcast.gray.2">Files processed. Click Next to continue.</Text>}
        {isFailed && (
          <Box>
            <Text fontWeight="600">Reason</Text>
            <Text color="red.500" role="alert">
              {job?.statusReason || 'No reason was reported by the job.'}
            </Text>
            <Text color="marketcast.gray.2" mt={1}>
              Fix the input files if needed, then start data processing again.
            </Text>
          </Box>
        )}

        <Flex gap={2} align="center" wrap="wrap">
          <Text fontWeight="600">Job ID</Text>
          <Code fontSize="xs" wordBreak="break-all">
            {jobId}
          </Code>
        </Flex>

        {formatTime(job?.startedAt) && (
          <Text color="marketcast.gray.2">
            Started {formatTime(job?.startedAt)}
            {formatTime(job?.stoppedAt) && ` · Finished ${formatTime(job?.stoppedAt)}`}
          </Text>
        )}

        {lastCheckedAt > 0 && <Text color="marketcast.gray.2">Last checked {formatTime(lastCheckedAt)}</Text>}

        {refreshError && (
          <Text color="red.500" role="alert">
            Couldn&apos;t refresh the status ({refreshError}). Retrying automatically.
          </Text>
        )}
      </Flex>
    </Box>
  );
};
